import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography } from '@mui/material';
import {
  getJobExecutions,
  getJobs,
  getThisWeekExecutions
} from '../../_services/ApiService';
import { DownloadButton, JobsTable } from './components';
import { ExecutionsToolbar } from './components/ExecutionsToolbar';
import { ExecutionsContext } from './components/ExecutionsContext';
import { useSearchParam } from 'helpers/useSearchParam';
import { jobResultType } from 'helpers/jobResultType';
import { Link, styled } from '@mui/material';
import { FullScreenLoader } from 'components/Loader/index';
import { InlineError } from 'components/Error';
import { displayStartTime } from 'components/JobExecution/executionTimes';

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(3)
}));

const Content = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2)
}));

const Page = ({ children }) => {
  return (
    <Root>
      <Content>{children}</Content>
    </Root>
  );
};

const PageTitle = ({ children }) => (
  <Typography variant="h1">{children}</Typography>
);

function getFilterOptions(executions = []) {
  const rawTypes = [];
  const rawCompanies = [];
  const rawPartners = [];

  for (const execution of executions) {
    rawTypes.push(execution.jobType);
    rawCompanies.push(execution.companyName?.toUpperCase());
    rawPartners.push(execution.partner?.toUpperCase() || undefined);
  }

  return {
    jobTypes: [...new Set(rawTypes.sort())],
    companies: [...new Set(rawCompanies.sort())],
    partners: [...new Set(rawPartners.sort())]
  };
}

function buildFilterQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      search.set(key, value);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

const JobsList = ({ jobs, title, pageSize }) => {
  const [jobNameFilter, setJobNameFilter] = useSearchParam('jobName');
  const [typeCriteria, setTypeCriteria] = useSearchParam('type');
  const [companyCriteria, setCompanyCriteria] = useSearchParam('company');
  const [partnerCriteria, setPartnerCriteria] = useSearchParam('partner');
  const [statusCriteria, setStatusCriteria] = useSearchParam('status');
  const [jobIdFilter, setJobIdFilter] = useSearchParam('jobId');
  const [startTimeFilter, setStartTimeFilter] = useSearchParam('from');
  const [toStartTimeFilter, settoStartTimeFilter] = useSearchParam('to');
  const [sortCriteria, setSortCriteria] = useState({
    field: 'startTime',
    direction: 'desc'
  });

  const checkCompanyFilter = (execution) => {
    const executionCompany = execution.companyName?.toUpperCase();
    if (companyCriteria === 'no-company') return !executionCompany;
    const checkCompany = companyCriteria && companyCriteria !== 'All';
    return checkCompany ? executionCompany === companyCriteria : true;
  };

  const checkPartnerFilter = (execution) => {
    const executionPartner = execution.partner?.toUpperCase();
    if (partnerCriteria === 'no-partner') return !executionPartner;
    const checkPartner = partnerCriteria && partnerCriteria !== 'All';
    return checkPartner ? executionPartner === partnerCriteria : true;
  };

  const sorter = (job1, job2) => {
    const sortValue = (job) =>
      sortCriteria.field === 'startTime'
        ? displayStartTime(job)
        : job[sortCriteria.field];
    const field1 = sortValue(job1);
    const field2 = sortValue(job2);
    const multiplier = sortCriteria.direction === 'desc' ? -1 : 1;
    if (field1 === field2) {
      return 0;
    } else if (field1 > field2) {
      return 1 * multiplier;
    } else {
      return -1 * multiplier;
    }
  };

  const filteredJobs = jobs
    .filter((execution) => {
      const checkType = typeCriteria && typeCriteria !== 'All';
      return checkType ? execution.jobType === typeCriteria : true;
    })
    .filter(checkCompanyFilter)
    .filter(checkPartnerFilter)
    .filter((execution) => {
      const checkStatus = statusCriteria && statusCriteria !== 'All';
      return checkStatus
        ? jobResultType(execution) === statusCriteria
        : true;
    })
    .filter((j) => j.jobName.indexOf(jobNameFilter || '') !== -1)
    .filter((j) => j.serviceId.indexOf(jobIdFilter || '') !== -1)
    .filter((j) => {
      if (!startTimeFilter && !toStartTimeFilter) {
        return true;
      }
      const startTimeDate = new Date(displayStartTime(j));
      if (startTimeFilter) {
        const from = new Date(startTimeFilter);
        from.setDate(from.getDate() + 1);
        from.setHours(0, 0, 0, 0);
        if (startTimeDate < from) return false;
      }
      if (toStartTimeFilter) {
        const to = new Date(toStartTimeFilter);
        to.setDate(to.getDate() + 1);
        to.setHours(23, 59, 59, 999);
        if (startTimeDate > to) return false;
      }
      return true;
    })
    .sort(sorter);

  const { jobTypes, companies, partners } = getFilterOptions(jobs);

  const clearAllFilters = () => {
    setJobNameFilter('');
    setTypeCriteria('');
    setCompanyCriteria('');
    setPartnerCriteria('');
    setStatusCriteria('');
    setJobIdFilter('');
    setStartTimeFilter('');
    settoStartTimeFilter('');
  };

  const contextValue = {
    jobTypes,
    companies,
    partners,
    onTypeChange: setTypeCriteria,
    onCompanyChange: setCompanyCriteria,
    onPartnerChange: setPartnerCriteria,
    onStatusChange: setStatusCriteria,
    onJobNameChange: setJobNameFilter,
    onJobIdChange: setJobIdFilter,
    onFromChange: setStartTimeFilter,
    onToChange: settoStartTimeFilter,
    onClearAll: clearAllFilters
  };

  return (
    <ExecutionsContext.Provider value={contextValue}>
      <ExecutionsToolbar />
      <JobsTable
        jobs={filteredJobs}
        pageSize={pageSize}
        setSortCriteria={setSortCriteria}
        sortCriteria={sortCriteria}
        title={title}
      />
    </ExecutionsContext.Provider>
  );
};

const useGetExecutions = (executionsFetcher) => {
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const source = axios.CancelToken.source();
    (async () => {
      setLoading(true);
      try {
        const [executionsResult, jobsResult] = await Promise.all([
          executionsFetcher(source.token),
          getJobs(source.token)
        ]);
        if (!cancelled) {
          const jobsById = new Map(jobsResult.map((job) => [job._id, job]));
          const enrichedExecutions = executionsResult.map((execution) => {
            const job = jobsById.get(execution.serviceId);
            return {
              ...execution,
              companyName: job?.config?.companyName,
              partner: job?.partner
            };
          });
          setJobs(enrichedExecutions);
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          console.log('request cancelled');
        } else {
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      source.cancel();
    };
  }, []);
  return { jobs, error, loading };
};

const WeekJobExecutionsListInner = () => {
  const { jobs, error, loading } = useGetExecutions(getThisWeekExecutions);
  if (loading) {
    return <FullScreenLoader />;
  }
  if (error) {
    return <InlineError error={error} />;
  }
  return <JobsList jobs={jobs} pageSize={200} />;
};

const AllTimeJobExecutionsListInner = () => {
  const { jobs, error, loading } = useGetExecutions(getJobExecutions);
  if (loading) {
    return <FullScreenLoader />;
  }
  if (error) {
    return <InlineError error={error} />;
  }
  return <JobsList jobs={jobs} pageSize={500} />;
};

const useFilterParams = () => {
  const [jobNameFilter] = useSearchParam('jobName');
  const [typeFilter] = useSearchParam('type');
  const [companyFilter] = useSearchParam('company');
  const [partnerFilter] = useSearchParam('partner');
  const [statusFilter] = useSearchParam('status');
  const [jobIdFilter] = useSearchParam('jobId');
  const [fromFilter] = useSearchParam('from');
  const [toFilter] = useSearchParam('to');

  return buildFilterQuery({
    jobName: jobNameFilter,
    type: typeFilter,
    company: companyFilter,
    partner: partnerFilter,
    status: statusFilter,
    jobId: jobIdFilter,
    from: fromFilter,
    to: toFilter
  });
};

export const WeekJobExecutionsList = () => {
  const filterQuery = useFilterParams();
  return (
    <Page>
      <PageTitle>
        Last 7 days of executions -{' '}
        <Link
          href={`/job_executions/all${filterQuery}`}
          title="View all time"
        >
          View all time
        </Link>
        <DownloadButton isWeek />
      </PageTitle>
      <WeekJobExecutionsListInner />
    </Page>
  );
};

export const AllTimeJobExecutionsList = () => {
  const filterQuery = useFilterParams();
  return (
    <Page>
      <PageTitle>
        All time executions -{' '}
        <Link
          href={`/job_executions${filterQuery}`}
          title="View last week"
        >
          View last 7 days
        </Link>
        <DownloadButton />
      </PageTitle>
      <AllTimeJobExecutionsListInner />
    </Page>
  );
};
